import { OrbitHandoffRequest, OrbitSelectRequest } from 'containers/Orbit/l4/types';
import { OrbitTurn, OrbitTurnResponse } from 'containers/Orbit/types';
import API from 'utils/request';

import {
  ORBIT_ASK_TIMEOUT_MS,
  OrbitMutationFailure,
  orbitFailureFromError,
  orbitTurnPath
} from './orbit';

export const orbitTurnSelectPath = (id: number) => `${orbitTurnPath(id)}/select`;
export const orbitTurnConfirmPath = (id: number) => `${orbitTurnPath(id)}/confirm`;
export const orbitTurnHandoffPath = (id: number) => `${orbitTurnPath(id)}/handoff_result`;

/**
 * 410 `41001`: the proposal is past `expires_at`, was replaced, or was never confirmable.
 * 409 from `handoff_result`: the proposal was already closed or expired; the record still exists.
 */
export type OrbitActionFailure =
  | OrbitMutationFailure
  | { kind: 'expired'; message: string | null }
  | { kind: 'conflict'; message: string | null };

export type OrbitActionOutcome = { kind: 'ok'; data: OrbitTurn } | OrbitActionFailure;

const postForTurn = async (path: string, body: object): Promise<OrbitActionOutcome> => {
  try {
    const response = await API.post<OrbitTurnResponse>(path, body, {
      timeout: ORBIT_ASK_TIMEOUT_MS
    });
    const turn = response?.data?.data;

    // The caller follows the returned id at once, so a body without one cannot count as a success.
    if (turn === undefined || turn === null || typeof turn.id !== 'number') {
      return { kind: 'technicalError', message: null };
    }

    return { kind: 'ok', data: turn };
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 410 || status === 409) {
      const message = error.response.data?.message;
      return {
        kind: status === 410 ? 'expired' : 'conflict',
        message: typeof message === 'string' ? message : null
      };
    }

    return orbitFailureFromError(error);
  }
};

/** 200 with a NEW turn that is already completed: the confirm card, or a refusal. */
export const selectOrbitCandidate = (turnId: number, body: OrbitSelectRequest) =>
  postForTurn(orbitTurnSelectPath(turnId), body);

/**
 * 202 with a NEW confirming turn to poll. The body never describes the action, and a repeat
 * returns the same confirming turn, so the server makes this exactly-once.
 */
export const confirmOrbitAction = (turnId: number) => postForTurn(orbitTurnConfirmPath(turnId), {});

/**
 * Reports the record the builder saved for a proposal. Answers 200 `{id, action}` of a NEW result
 * turn; when the body is only that, the whole turn is read so the panel can draw it at once.
 */
export const reportOrbitHandoff = async (
  turnId: number,
  body: OrbitHandoffRequest
): Promise<OrbitActionOutcome> => {
  const outcome = await postForTurn(orbitTurnHandoffPath(turnId), body);
  if (outcome.kind !== 'ok' || typeof outcome.data.state === 'string') {
    return outcome;
  }
  try {
    const response = await API.get<OrbitTurnResponse>(orbitTurnPath(outcome.data.id), {
      timeout: ORBIT_ASK_TIMEOUT_MS
    });
    const turn = response?.data?.data;
    return turn ? { kind: 'ok', data: turn } : { kind: 'technicalError', message: null };
  } catch (error) {
    return orbitFailureFromError(error);
  }
};
